import type { CalibrationProfile } from './calibration-profile';

/**
 * Which camera a training was measured through, stamped when the measurements are finished (S2-5).
 *
 * Architecture §20's first reuse rule is "reuse when the camera is unchanged", and the predicate can
 * only fire if the profile records the camera it was measured on. A browser withholds the device id
 * until permission is granted, and hero training is reached from a *stopped* session — the readiness
 * screen ended its stream on the way out — so the id does not exist when a training screen is built.
 * It exists by the time the training is finished, which is the only honest moment to stamp it.
 *
 * **Session-only, and it stays that way.** The id lives on a profile in the sitting's own calibration
 * store and dies with the sitting; there is no `thrive.v1.*` key for it and there must not be — a
 * device identifier is a fact about a household's hardware.
 *
 * A session that cannot say which camera it is looking through leaves the profile as it found it:
 * an unknown id on either side is never evidence of a change (see `evaluateCalibrationReuse`).
 */
export function stampCameraDeviceId(
  profile: CalibrationProfile,
  camera: { readonly activeCameraDeviceId: string | null },
): CalibrationProfile {
  const deviceId = camera.activeCameraDeviceId;
  if (deviceId === null || profile.cameraDeviceId === deviceId) return profile;
  return { ...profile, cameraDeviceId: deviceId };
}
